"use client";

import { useEffect, useMemo, useState } from "react";
import { IoClose, IoInformationCircleOutline } from "react-icons/io5";
import mainAPI from "../../../services/main/mainAPI";
import { formatCompact2DateTime } from "../../../utils/formatDatetime";
import CapacityOverview from "../../shared/CapacityOverview";

type CapacityPercent = {
  skillListeningSpeaking: number | null;
  skillLangNature: number | null;
  skillReading: number | null;
  skillWriting: number | null;
};

type PopularGameRow = {
  id: number | string;
  uuid: string;
  exerciseName: string;
  createBy: string;
  updateAt: string;
  studentPlayedCount: number;
};

const TOP_LIST_LIMIT = 10;
const POPUP_PAGE_SIZE = 20;

function buildPaginationItems(currentPage: number, totalPages: number): Array<number | "ellipsis"> {
  if (totalPages <= 6) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, 4, "ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "ellipsis", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
}

function normalizeCapacityPercent(response: unknown): CapacityPercent {
  const rawResponse = response as {
    capacity_percent?: {
      skill_listening_speaking?: unknown;
      skill_lang_nature?: unknown;
      skill_reading?: unknown;
      skill_writing?: unknown;
    };
    capacityPercent?: {
      skillListeningSpeaking?: unknown;
      skillLangNature?: unknown;
      skillReading?: unknown;
      skillWriting?: unknown;
    };
    data?: {
      capacity_percent?: {
        skill_listening_speaking?: unknown;
        skill_lang_nature?: unknown;
        skill_reading?: unknown;
        skill_writing?: unknown;
      };
    };
  };

  const capacityPercent = rawResponse.capacity_percent ?? rawResponse.data?.capacity_percent;
  const camelCaseCapacityPercent = rawResponse.capacityPercent;

  const toNullableNumber = (value: unknown) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return {
    skillListeningSpeaking: toNullableNumber(
      capacityPercent?.skill_listening_speaking ?? camelCaseCapacityPercent?.skillListeningSpeaking,
    ),
    skillLangNature: toNullableNumber(
      capacityPercent?.skill_lang_nature ?? camelCaseCapacityPercent?.skillLangNature,
    ),
    skillReading: toNullableNumber(
      capacityPercent?.skill_reading ?? camelCaseCapacityPercent?.skillReading,
    ),
    skillWriting: toNullableNumber(
      capacityPercent?.skill_writing ?? camelCaseCapacityPercent?.skillWriting,
    ),
  };
}

function normalizePopularGameRows(response: unknown): PopularGameRow[] {
  const sourceRows = Array.isArray(response)
    ? response
    : Array.isArray((response as { data?: unknown[] })?.data)
      ? (response as { data: unknown[] }).data
      : [];

  return sourceRows
    .map((item) => {
      const row = item as {
        id?: unknown;
        uuid?: unknown;
        exercise_name?: unknown;
        exerciseName?: unknown;
        create_by?: unknown;
        createBy?: unknown;
        create_at?: unknown;
        createAt?: unknown;
        update_at?: unknown;
        updateAt?: unknown;
        student_played_count?: unknown;
        studentPlayedCount?: unknown;
      };

      const exerciseName = String(row.exercise_name ?? row.exerciseName ?? "").trim();
      const studentPlayedCount = Number(row.student_played_count ?? row.studentPlayedCount);

      if (!exerciseName || !Number.isFinite(studentPlayedCount) || studentPlayedCount <= 0) {
        return null;
      }

      const rawId = row.id;
      const id =
        typeof rawId === "number" || typeof rawId === "string"
          ? rawId
          : String(rawId ?? "").trim() || "-";

      const createByRaw = row.create_by ?? row.createBy;
      const createBy = createByRaw === null || createByRaw === undefined
        ? "-"
        : String(createByRaw).trim() || "-";

      const updateAt = String(row.update_at ?? row.updateAt ?? "").trim();
      return {
        id,
        uuid: String(row.uuid ?? "").trim(),
        exerciseName,
        createBy,
        updateAt,
        studentPlayedCount,
      };
    })
    .filter(Boolean) as PopularGameRow[];
}

export default function PoppularGame() {
  const [rows, setRows] = useState<PopularGameRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAllPopupOpen, setIsAllPopupOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedGame, setSelectedGame] = useState<PopularGameRow | null>(null);
  const [selectedCapacity, setSelectedCapacity] = useState<CapacityPercent | null>(null);
  const [isCapacityLoading, setIsCapacityLoading] = useState(false);

  const loadPopularGames = async () => {
    setIsLoading(true);
    const response = await mainAPI.getAllPopularGames();

    if (!response || (response as { status?: string })?.status === "failed") {
      setRows([]);
      setIsLoading(false);
      return;
    }

    const normalizedRows = normalizePopularGameRows(response).sort(
      (a, b) => b.studentPlayedCount - a.studentPlayedCount,
    );
    setRows(normalizedRows);
    setIsLoading(false);
  };

  useEffect(() => {
    void loadPopularGames();
  }, []);

  useEffect(() => {
    if (!isAllPopupOpen && !selectedGame) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isAllPopupOpen, selectedGame]);

  useEffect(() => {
    if (!selectedGame?.uuid) {
      setSelectedCapacity(null);
      setIsCapacityLoading(false);
      return;
    }

    let isMounted = true;
    setIsCapacityLoading(true);
    setSelectedCapacity(null);

    void mainAPI.getCapacityByUuid(selectedGame.uuid).then((response) => {
      if (!isMounted) {
        return;
      }

      if (!response || (response as { status?: string })?.status === "failed") {
        setSelectedCapacity({
          skillListeningSpeaking: null,
          skillLangNature: null,
          skillReading: null,
          skillWriting: null,
        });
      } else {
        setSelectedCapacity(normalizeCapacityPercent(response));
      }

      setIsCapacityLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [selectedGame]);

  const topRows = useMemo(() => rows.slice(0, TOP_LIST_LIMIT), [rows]);
  const totalPages = Math.max(1, Math.ceil(rows.length / POPUP_PAGE_SIZE));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * POPUP_PAGE_SIZE;
    return rows.slice(start, start + POPUP_PAGE_SIZE);
  }, [rows, currentPage]);
  const paginationItems = useMemo(
    () => buildPaginationItems(currentPage, totalPages),
    [currentPage, totalPages],
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const renderTable = (tableRows: PopularGameRow[], usePopupIndex = false) => {
    if (isLoading) {
      return <div className="px-4 py-6 text-sm text-[#7a7a7a]">กำลังโหลด...</div>;
    }

    if (tableRows.length === 0) {
      return <div className="px-4 py-6 text-sm text-[#7a7a7a]">ไม่พบข้อมูล</div>;
    }

    return (
      <div className="overflow-x-auto px-4 pb-3">
        <table className="min-w-full border-separate border-spacing-y-0">
          <thead>
            <tr className="text-left text-sm font-semibold text-[#5a5a5a]">
              <th className="w-14 px-4 py-2"></th>
              <th className="px-4 py-2">ชื่อเกม</th>
              <th className="px-4 py-2">ผู้สร้าง</th>
              <th className="px-4 py-2">อัปเดตล่าสุด</th>
              <th className="w-40 px-4 py-2 text-right">จำนวนการใช้</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, index) => {
              const rowNumber = usePopupIndex
                ? (currentPage - 1) * POPUP_PAGE_SIZE + index + 1
                : index + 1;
              const isEven = index % 2 === 1;

              return (
                <tr
                  key={`${row.uuid || row.id}_${rowNumber}`}
                  className={isEven ? "bg-[#ECEFF6]" : "bg-[#F7F7F7]"}
                >
                  <td className="px-4 py-2 text-lg font-normal text-[#4E4E4E]">{rowNumber}</td>
                  <td className="px-4 py-2 text-lg font-normal text-[#4E4E4E]">{row.exerciseName}</td>
                  <td className="px-4 py-2 text-lg font-normal text-[#4E4E4E]">{row.createBy}</td>
                  <td className="px-4 py-2 text-lg font-normal text-[#4E4E4E]">
                    {row.updateAt ? formatCompact2DateTime(row.updateAt) : "-"}
                  </td>
                  <td className="px-4 py-2 text-right text-lg font-normal text-[#4E4E4E]">
                    <div className="flex items-center justify-end gap-4">
                      <span>{row.studentPlayedCount}</span>
                      <button
                        type="button"
                        onClick={() => setSelectedGame(row)}
                        className="cursor-pointer text-[#0092D7] hover:text-[#0079b3]"
                        title="ข้อมูลเกม"
                        aria-label={`ข้อมูลเกม ${row.exerciseName}`}
                      >
                        <IoInformationCircleOutline size={19} className="mt-0.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div>
        <div className="flex items-center justify-between px-4 pt-4">
          <h1 className="text-lg font-bold text-[#4E4E4E]">อันดับเกมยอดนิยมจากผู้ใช้</h1>
          <button
            type="button"
            onClick={() => {
              setCurrentPage(1);
              setIsAllPopupOpen(true);
            }}
            className="cursor-pointer text-base font-normal text-[#0092D7] hover:underline"
          >
            ดูทั้งหมด
          </button>
        </div>

        {renderTable(topRows)}
      </div>

      {isAllPopupOpen ? (
        <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-225 rounded-2xl bg-white shadow-[0_12px_35px_rgba(0,0,0,0.35)]">
            <div className="flex items-center justify-between px-5 py-4">
              <h2 className="text-xl font-semibold text-[#4E4E4E]">อันดับเกมยอดนิยมจากผู้ใช้</h2>
              <button
                type="button"
                onClick={() => setIsAllPopupOpen(false)}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-[#d7dade] text-[#4E4E4E] transition hover:bg-[#c1c8d1]"
              >
                <IoClose size={22} />
              </button>
            </div>

            <div className="max-h-[80vh] overflow-y-auto pt-2">
              {renderTable(paginatedRows, true)}

              {!isLoading && rows.length > 0 ? (
                <div className="flex justify-center px-4 pb-5 pt-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      className="h-8 min-w-8 cursor-pointer rounded border border-[#d9d9d9] px-2 text-sm text-[#4E4E4E] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      ก่อนหน้า
                    </button>

                    {paginationItems.map((item, index) => (
                      item === "ellipsis" ? (
                        <span key={`ellipsis_${index}`} className="px-1 text-[#8a8a8a]">...</span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setCurrentPage(item)}
                          className={`h-8 min-w-8 cursor-pointer rounded border px-2 text-sm ${item === currentPage
                            ? "border-[#0092D7] bg-[#0092D7] text-white"
                            : "border-[#d9d9d9] text-[#4E4E4E]"}`}
                        >
                          {item}
                        </button>
                      )
                    ))}

                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      className="h-8 min-w-8 cursor-pointer rounded border border-[#d9d9d9] px-2 text-sm text-[#4E4E4E] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      ถัดไป
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {selectedGame ? (
        <div className="fixed inset-0 z-80 flex items-center justify-center bg-black/45 p-4">
          <div className="flex h-[780px] w-full max-w-172.5 flex-col rounded-2xl bg-white shadow-[0_12px_35px_rgba(0,0,0,0.35)]">
            <div className="flex items-center justify-between px-5 py-4">
              <h2 className="wrap-break-word text-xl font-semibold leading-tight text-[#0092D7]">
                สมรรถนะเฉลี่ยของเกม {selectedGame.exerciseName}
              </h2>
              <button
                type="button"
                onClick={() => setSelectedGame(null)}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-[#d7dade] text-[#4E4E4E] transition hover:bg-[#c1c8d1]"
              >
                <IoClose size={22} />
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col px-5 pb-5 pt-0">
              {isCapacityLoading ? (
                <div className="flex flex-1 items-center justify-center text-sm text-[#7a7a7a]">
                  กำลังโหลด...
                </div>
              ) : (
                <CapacityOverview
                  capacityPercent={selectedCapacity ?? {
                    skillListeningSpeaking: null,
                    skillLangNature: null,
                    skillReading: null,
                    skillWriting: null,
                  }}
                  variant="popup"
                  className="h-full"
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
